import type { ControlPlaneSummary, ProcessingStageKind } from "../domain";

/**
 * What the indicator says at a glance.
 *
 * - `blocked` — part of the pipeline has stopped and has not recovered. Leads
 *   every other state because it is the only one the counts cannot express: a
 *   lane that is refusing every job empties its own queue, so on 2026-07-28 a
 *   five-hour outage read as "nothing in progress" here for five hours.
 * - `unknown` — the control plane reported no processing backlog at all. An
 *   honest "not established", never a fabricated zero.
 * - `working` — something is waiting or in flight right now.
 * - `unfinished` — nothing is moving, but items stopped without completing.
 * - `idle` — the backlog was read and it is empty.
 */
export type ActivityStateKind =
  "blocked" | "unknown" | "working" | "unfinished" | "idle";

/** The movement state on its own, with a blocker set aside. */
export type ActivityFlowKind = Exclude<ActivityStateKind, "blocked">;

export type ActivityStageKey =
  "collect" | "read" | "understand" | "publish" | "other";

export interface ActivityStageCounts {
  readonly key: ActivityStageKey;
  readonly inProgress: number;
  readonly waiting: number;
  readonly unfinished: number;
}

/**
 * One stopped area of the member's own processing, in the same plain-language
 * groups the breakdown uses. Two of them never describe the same thing: a
 * blocker the member has to act on (the account connection lapsed) is separate
 * from one they cannot, because only the first is worth telling them to do
 * something about.
 */
export interface ActivityBlocker {
  readonly stage: ActivityStageKey;
  /** True when the member's own account connection is what has to be renewed. */
  readonly needsAccountRenewal: boolean;
  /** When this blocker started — never the time it was read. */
  readonly since: string;
}

export interface ActivitySnapshot {
  readonly kind: ActivityStateKind;
  /**
   * What is moving, with any blocker set aside. The headline `kind` is taken
   * over by a blocker when there is one, and the panel still has to be able to
   * say whether anything is running underneath it.
   */
  readonly flow: ActivityFlowKind;
  readonly inProgress: number;
  readonly waiting: number;
  readonly unfinished: number;
  readonly recentCompleted: number;
  /** Only the groups carrying work; an empty list means nothing to break down. */
  readonly stages: readonly ActivityStageCounts[];
  /**
   * Stopped areas that have not recovered. Empty when the record was read and
   * nothing is blocked, and also when it could not be read at all — an
   * unreadable record is not evidence of a blocker, so nothing is claimed.
   */
  readonly blockers: readonly ActivityBlocker[];
  /** When this observation was read — always stated, including for `unknown`. */
  readonly asOf: string;
  readonly lastCompletedAt: string | null;
}

/**
 * The plain-language groups the indicator draws, and the internal pipeline
 * stages each one covers. The member never sees the internal names; grouping
 * here keeps that mapping in one place. `other` is the deliberate catch-all: a
 * stage added to the contract later still shows up as work rather than
 * disappearing from the breakdown while the totals count it.
 */
const STAGE_GROUPS: readonly {
  readonly key: Exclude<ActivityStageKey, "other">;
  readonly kinds: readonly ProcessingStageKind[];
}[] = [
  { key: "collect", kinds: ["source_inventory", "source_graph"] },
  {
    key: "read",
    kinds: [
      "canonical_extraction",
      "document_extraction",
      "media_understanding",
    ],
  },
  { key: "understand", kinds: ["knowledge_extraction", "fact_resolution"] },
  { key: "publish", kinds: ["projection_refresh", "coverage_reconciliation"] },
];

const STAGE_ORDER: readonly ActivityStageKey[] = [
  ...STAGE_GROUPS.map((group) => group.key),
  "other",
];

function groupOf(kind: ProcessingStageKind): ActivityStageKey {
  return (
    STAGE_GROUPS.find((group) => group.kinds.includes(kind))?.key ?? "other"
  );
}

/**
 * Derives the indicator's whole state from the control-plane summary the shell
 * already holds — no extra request, no extra endpoint.
 *
 * Returns `null` when there is nothing to observe: no summary yet, or no
 * connection at all. In both cases the indicator renders nothing rather than
 * occupying the rail with a claim it cannot support.
 */
export function activitySnapshot(
  summary: ControlPlaneSummary | null,
  checkedAt: Date | null,
): ActivitySnapshot | null {
  if (!summary || summary.state === "empty") return null;
  const blockers = activityBlockers(summary);
  const flow = summary.processingFlow;
  if (!flow) {
    return {
      // A backlog that could not be read does not hide a blocker that could:
      // the two come from different surfaces, so an unknown backlog still
      // reports a known blocker.
      kind: blockers.length > 0 ? "blocked" : "unknown",
      flow: "unknown",
      inProgress: 0,
      waiting: 0,
      unfinished: 0,
      recentCompleted: 0,
      stages: [],
      blockers,
      asOf: (checkedAt ?? new Date(summary.updatedAt)).toISOString(),
      lastCompletedAt: null,
    };
  }
  const byGroup = new Map<ActivityStageKey, ActivityStageCounts>();
  let inProgress = 0;
  let waiting = 0;
  let unfinished = 0;
  let recentCompleted = 0;
  for (const stage of flow.stages) {
    inProgress += stage.processing;
    waiting += stage.pending;
    unfinished += stage.failed;
    recentCompleted += stage.recentCompleted;
    const key = groupOf(stage.kind);
    const total = byGroup.get(key) ?? {
      key,
      inProgress: 0,
      waiting: 0,
      unfinished: 0,
    };
    byGroup.set(key, {
      key,
      inProgress: total.inProgress + stage.processing,
      waiting: total.waiting + stage.pending,
      unfinished: total.unfinished + stage.failed,
    });
  }
  const stages = STAGE_ORDER.flatMap((key) => {
    const group = byGroup.get(key);
    if (!group) return [];
    return group.inProgress + group.waiting + group.unfinished > 0
      ? [group]
      : [];
  });
  const flowKind: ActivityFlowKind =
    inProgress + waiting > 0
      ? "working"
      : unfinished > 0
        ? "unfinished"
        : "idle";
  return {
    // A blocker outranks movement. Work continuing in one lane says nothing
    // about a lane that has stopped, and the stopped one is the fact a member
    // would otherwise never learn.
    kind: blockers.length > 0 ? "blocked" : flowKind,
    flow: flowKind,
    inProgress,
    waiting,
    unfinished,
    recentCompleted,
    stages,
    blockers,
    asOf: flow.asOf,
    lastCompletedAt: flow.lastCompletedAt,
  };
}

/**
 * Folds the summary's blockers into the plain-language groups the panel draws.
 * Several internal stages share one group, so several blockers can collapse
 * into one line; the earliest start wins, because "since when" is the fact and
 * the later occurrence is a repeat of it.
 */
function activityBlockers(summary: ControlPlaneSummary): ActivityBlocker[] {
  const byKey = new Map<string, ActivityBlocker>();
  for (const blocker of summary.blockers ?? []) {
    const stage = groupOf(blocker.stage);
    const needsAccountRenewal = blocker.kind === "reauthorization_required";
    const key = `${stage}\u001f${String(needsAccountRenewal)}`;
    const existing = byKey.get(key);
    if (!existing || blocker.since < existing.since) {
      byKey.set(key, { stage, needsAccountRenewal, since: blocker.since });
    }
  }
  // Drawn in pipeline order so the panel reads the way the work flows, with a
  // renewal the member can act on ahead of one they cannot within each group.
  return [...byKey.values()].sort((left, right) => {
    const order =
      STAGE_ORDER.indexOf(left.stage) - STAGE_ORDER.indexOf(right.stage);
    if (order !== 0) return order;
    return Number(right.needsAccountRenewal) - Number(left.needsAccountRenewal);
  });
}

/**
 * "4 minutes ago" in the member's locale. Coarse on purpose: the indicator
 * reports an observation, not a precise clock, and a rounded distance is the
 * honest resolution for a value that refreshes on a poll.
 */
export function relativeTime(
  value: string,
  now: Date,
  locale: string,
): string | null {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return null;
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const minutes = Math.round((now.getTime() - parsed) / 60_000);
  if (Math.abs(minutes) < 60) return format.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return format.format(-hours, "hour");
  return format.format(-Math.round(hours / 24), "day");
}

export function clockTime(value: string, locale: string): string | null {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { timeStyle: "short" }).format(parsed);
}
